<script setup lang="ts">
import { computed } from 'vue'
import { useInbox, type PanelTab } from '../../stores/inbox'
import AiModeCard from '../aimode/AiModeCard.vue'
import Icon from '../ui/Icon.vue'
import type { IconName } from '../ui/icons'
import CustomerTab from './CustomerTab.vue'
import HistoryTab from './HistoryTab.vue'
import OrderTab from './OrderTab.vue'
import MenuGrid from './order/MenuGrid.vue'
import { useMenuRead } from '../../stores/menuRead'

const store = useInbox()

const hasCart = computed(() => !!store.selectedDetail?.ai?.cart)
const tabs: { id: PanelTab; label: string; icon: IconName }[] = [
  { id: 'customer', label: 'ลูกค้า', icon: 'user' },
  { id: 'order', label: 'ลงออเดอร์', icon: 'cart' },
  { id: 'history', label: 'ประวัติ', icon: 'history' },
]
const name = computed(() => store.selected?.contact.name ?? '')
// ลงออเดอร์ › "ขยาย": the menu grid takes half the screen next to the chat (stores/menuRead.ts)
const mr = useMenuRead()
const wide = computed(() => !!store.selectedDetail && mr.wideConv === store.selectedDetail.id)
</script>

<template>
  <aside class="side" :class="{ wide }" :aria-label="name ? `แผงลูกค้า ${name}` : 'แผงลูกค้า'">
    <MenuGrid v-if="wide && store.selectedDetail" :conv-id="store.selectedDetail.id" />
    <div v-else class="op-top">
      <div class="ptabs" role="tablist" aria-label="แผงลูกค้า">
        <button
          v-for="t in tabs"
          :key="t.id"
          class="ptab"
          :class="{ on: store.panelTab === t.id }"
          role="tab"
          :aria-selected="store.panelTab === t.id"
          :disabled="!store.selectedDetail"
          @click="store.panelTab = t.id"
        >
          <Icon :name="t.icon" /><span>{{ t.label }}</span>
          <template v-if="t.id === 'order' && hasCart">
            <span class="ptab-dot" aria-hidden="true"></span><span class="sr">มีตะกร้าที่ AI เตรียมไว้</span>
          </template>
        </button>
      </div>
    </div>

    <div v-if="!wide && !store.selectedDetail" class="pane-empty">
      <p>{{ store.selectedId ? 'กำลังโหลดข้อมูลลูกค้า…' : 'ข้อมูลลูกค้าจะแสดงที่นี่เมื่อเลือกแชท' }}</p>
    </div>
    <template v-else-if="!wide && store.selectedDetail">
      <AiModeCard :conv-id="store.selectedDetail.id" />
      <CustomerTab v-if="store.panelTab === 'customer'" :detail="store.selectedDetail" />
      <OrderTab v-else-if="store.panelTab === 'order'" :detail="store.selectedDetail" />
      <HistoryTab v-else :detail="store.selectedDetail" />
    </template>
  </aside>
</template>

<style scoped>
.side {
  width: clamp(400px, 31.4vw, 452px);
  flex: none;
  background: #fff;
  border-left: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.side.wide {
  /* half of the screen right of the rail; the chat keeps the other half (InboxView hides the chat list) */
  width: calc((100vw - 64px) / 2);
}
@media (max-width: 760px) {
  .side.wide {
    position: fixed;
    inset: 0;
    z-index: 40;
    width: auto;
  }
}
.op-top {
  height: 68px;
  flex: none;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-bottom: 1px solid var(--line);
}
.ptabs {
  display: flex;
  width: 100%;
  padding: 3px;
  background: #eef2f0;
  border-radius: 10px;
  gap: 2px;
}
.ptab {
  flex: 1 1 0;
  min-width: 0;
  height: 36px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  color: #3e4d46;
}
.ptab:hover:not(:disabled) {
  color: var(--ink);
  background: rgba(255, 255, 255, 0.55);
}
.ptab.on {
  background: #fff;
  color: var(--ink);
  font-weight: 600;
  box-shadow:
    0 1px 2px rgba(23, 33, 28, 0.08),
    0 0 0 1px rgba(23, 33, 28, 0.04);
}
.ptab.on:hover {
  background: #fff;
}
.ptab :deep(.ic) {
  width: 16px;
  height: 16px;
}
.ptab.on :deep(.ic) {
  color: var(--ac);
}
.ptab-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ac);
  flex: none;
}
.pane-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: var(--mut);
  font-size: 13px;
  line-height: 20px;
  text-align: center;
}
</style>
